import { isBaseBlueprint, type RepoAccess } from '@runloop/reflex-contract';
import type { Blueprint } from '@/lib/types';

/**
 * Repo handed off from the launch dialog's "Create a blueprint" nudge to the
 * blueprint create form through `location.state`. Centralized so the writer
 * (the launch dialog) and the reader (the create page) can't drift.
 */
export interface BlueprintPrefillRepoState {
  repoSlug: string;
  repoBranch?: string;
  repoAccess?: RepoAccess;
}

export const BLUEPRINT_LOAD_ERROR_TITLE = "Couldn't load blueprints";
export const BLUEPRINT_LOAD_ERROR_DESCRIPTION =
  'Check the organization Runloop API key in Secrets & API Keys.';

/**
 * Derive a blueprint name from a GitHub repo slug for the "New Repo Image"
 * flow, where the name is auto-generated rather than typed.
 *
 * Takes the repository segment (after the last `/`), lowercases it, and
 * collapses any run of non-alphanumeric characters into a single `-`,
 * trimming stray dashes — e.g. `owner/Reflex-App` -> `reflex-app`.
 *
 * The result must NOT read as a base blueprint (a repo image builds on a
 * base; it is not itself one), so if slugification yields an empty string or
 * a base-like name (`base`, or anything containing `_base` — see
 * {@link isBaseBlueprint}), we append `-image` to disambiguate.
 */
export function deriveBlueprintNameFromRepo(repoSlug: string): string {
  const segment = repoSlug.split('/').pop() ?? '';
  const slug = segment
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!slug || isBaseBlueprint({ name: slug, metadata: undefined })) {
    return slug ? `${slug}-image` : 'repo-image';
  }
  return slug;
}

export function getLatestByName(blueprints: Blueprint[]): Blueprint[] {
  const byName = new Map<string, Blueprint>();
  for (const bp of blueprints) {
    const existing = byName.get(bp.name);
    if (!existing || bp.createTimeMs > existing.createTimeMs) {
      byName.set(bp.name, bp);
    }
  }
  return Array.from(byName.values()).sort((a, b) => b.createTimeMs - a.createTimeMs);
}
